/**
 * Better Auth Admin access control for Harmoni staff IAM (#575).
 * Client-safe: no server-only imports. Import from both API and web.
 *
 * Impersonation is intentionally omitted from all roles until product sign-off.
 */
import { createAccessControl } from 'better-auth/plugins/access'
import {
  adminAc,
  defaultStatements,
  userAc,
} from 'better-auth/plugins/admin/access'

const statement = {
  ...defaultStatements,
  console: ['access'],
  song: ['moderate', 'restore'],
} as const

export const ac = createAccessControl(statement)

/** Admin default user actions without impersonate (product non-goal for now). */
const staffUserActions = adminAc.statements.user.filter(
  (a) => a !== 'impersonate',
)

/** Bootstrap / break-glass — full admin statements + console + content. */
export const superadmin = ac.newRole({
  ...adminAc.statements,
  console: ['access'],
  song: ['moderate', 'restore'],
  user: staffUserActions,
})

/** Staff ops: user/session admin without full superadmin narrative. */
export const admin = ac.newRole({
  ...adminAc.statements,
  console: ['access'],
  song: ['moderate', 'restore'],
  user: staffUserActions,
})

/** Content moderation + console; no ban/set-role/delete user. */
export const moderator = ac.newRole({
  console: ['access'],
  song: ['moderate', 'restore'],
  user: ['list', 'get'],
  session: ['list'],
})

/** Product user — no staff Admin statements. */
export const user = ac.newRole({
  ...userAc.statements,
})

export const roles = {
  superadmin,
  admin,
  moderator,
  user,
} as const

export type StaffAuthRole = keyof typeof roles

export const STAFF_ROLES = ['superadmin', 'admin', 'moderator'] as const
export type StaffRoleName = (typeof STAFF_ROLES)[number]

export function isStaffRole(
  role: string | null | undefined,
): role is StaffRoleName {
  return role === 'superadmin' || role === 'admin' || role === 'moderator'
}

/** Admin-only oRPC / shell (includes superadmin). */
export function isAdminRole(role: string | null | undefined): boolean {
  return role === 'admin' || role === 'superadmin'
}

/** Moderator or higher. */
export function isModeratorOrHigher(role: string | null | undefined): boolean {
  return isStaffRole(role)
}

/** Primary role when BA stores comma-separated multi-roles. */
export function primaryRole(role: string | null | undefined): string {
  if (!role) return 'user'
  return role.split(',')[0]?.trim() || 'user'
}

/**
 * Self-review break-glass (#1133): only superadmin may decide their own
 * contribution. Solo-operated environments would otherwise deadlock the
 * review queue. Every bypass is written to `admin_audit_log` as
 * `self_review: true`.
 */
export function canSelfReview(role: string | null | undefined): boolean {
  return primaryRole(role) === 'superadmin'
}

/**
 * Self-claim break-glass on the chart request board (#1202). Same policy as
 * {@link canSelfReview}: a solo-operated instance would otherwise deadlock its
 * own board, since the superadmin is both author and only volunteer. Every
 * bypass is written to `admin_audit_log` as `self_claim: true`.
 */
export function canSelfClaim(role: string | null | undefined): boolean {
  return canSelfReview(role)
}
